import { useForm } from '@tanstack/react-form';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import type { GetAdminCourse } from '@workspace/client';
import {
    Button,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Skeleton,
    Switch,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Textarea,
} from '@workspace/shadcn';
import {
    DateField,
    FieldMessage,
    FormMessage,
    ImageField,
    MultiSelect,
    errorToFormIssue,
    useAction,
    useData,
} from '@workspace/ui';

import { PageHeader } from '../../components/page-header.tsx';
import { TextListField } from '../../components/text-list-field.tsx';
import { priceField, toHalalas, toSar } from './price.ts';

const LEVELS = [
  { value: 'INTRODUCTORY', label: 'Introductory' },
  { value: 'INTERMEDIATE', label: 'Intermediate' },
  { value: 'PROFESSIONAL', label: 'Professional' },
] as const;

const COURSE_TYPES = [
  { value: 'ONLINE', label: 'Online' },
  { value: 'IN_PERSON', label: 'In-person' },
] as const;

/** Stands in for "no selection" — Radix Select cannot hold an empty string. */
const UNSET = '__unset__';

const schema = z.object({
  title: z.string().min(1, { message: 'Title is required' }),
  oneLiner: z.string(),
  description: z.string().min(1, { message: 'Description is required' }),
  category: z.string(),
  level: z.enum(['INTRODUCTORY', 'INTERMEDIATE', 'PROFESSIONAL']),
  price: priceField,
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  durationHours: z.number().nullable(),
  trainingMethod: z.enum(['ONLINE', 'IN_PERSON']).nullable(),
  location: z.string(),
  languages: z.array(z.string()),
  objectives: z.array(z.string()),
  targetedAudience: z.string(),
  targetedAreas: z.array(z.string()),
  prerequisites: z.string(),
  imageUrl: z.string().min(1, { message: 'Course image is required' }),
  instructorIds: z.array(z.string()),
  published: z.boolean(),
  featured: z.boolean(),
  comingSoon: z.boolean(),
  showModules: z.boolean(),
}).refine(
  (value) =>
    !value.startDate || !value.endDate || value.endDate >= value.startDate,
  { message: 'End date must not precede the start date', path: ['endDate'] },
);

/**
 * The generated client widens nullable columns to non-nullable, so every
 * optional value is normalised here rather than at each field.
 */
function toDefaults(course?: GetAdminCourse) {
  return {
    title: course?.title ?? '',
    oneLiner: course?.oneLiner ?? '',
    description: course?.description ?? '',
    category: course?.category ?? '',
    level: course?.level ?? 'INTRODUCTORY',
    price: course ? toSar(course.price) : Number.NaN,
    startDate: course?.startDate ?? null,
    endDate: course?.endDate ?? null,
    durationHours: course?.durationHours ?? null,
    trainingMethod: course?.trainingMethod ?? null,
    location: course?.location ?? '',
    languages: course?.languages ?? [],
    objectives: course?.objectives ?? [],
    targetedAudience: course?.targetedAudience ?? '',
    targetedAreas: course?.targetedAreas ?? [],
    prerequisites: course?.prerequisites ?? '',
    imageUrl: course?.imageUrl ?? '',
    instructorIds: course?.instructors?.map((one) => one.id) ?? [],
    published: course?.published ?? false,
    featured: course?.featured ?? false,
    comingSoon: course?.comingSoon ?? false,
    showModules: course?.showModules ?? false,
  };
}

/** Empty text means "cleared"; the API stores null and the site hides the row. */
function orNull(value: string) {
  return value.trim() ? value.trim() : null;
}

function toPayload(value: ReturnType<typeof toDefaults>) {
  return {
    title: value.title,
    oneLiner: orNull(value.oneLiner),
    description: value.description,
    category: orNull(value.category),
    level: value.level,
    price: toHalalas(value.price),
    startDate: value.startDate,
    endDate: value.endDate,
    durationHours: value.durationHours,
    trainingMethod: value.trainingMethod,
    location: orNull(value.location),
    languages: value.languages.map((one) => one.trim()).filter(Boolean),
    objectives: value.objectives.map((one) => one.trim()).filter(Boolean),
    targetedAudience: orNull(value.targetedAudience),
    targetedAreas: value.targetedAreas.map((one) => one.trim()).filter(Boolean),
    prerequisites: orNull(value.prerequisites),
    imageUrl: value.imageUrl,
    instructorIds: value.instructorIds,
    published: value.published,
    featured: value.featured,
    comingSoon: value.comingSoon,
    showModules: value.showModules,
  };
}

export default function CourseEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const courseQuery = useData('GET /admin/courses/{id}', { id: id! }, {
    enabled: Boolean(id),
  });

  if (id && courseQuery.isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-[480px] w-full" />
      </div>
    );
  }

  // Without this a deleted or mistyped id would fall through to a blank form
  // and silently create a second course on save.
  if (id && !courseQuery.data) {
    return (
      <div className="py-8">
        <PageHeader
          title="Course not found"
          description="This course no longer exists, or the link is wrong."
          icon={BookOpen}
        />
        <Button variant="outline" onClick={() => navigate('/courses')}>
          <ArrowLeft className="me-1 size-4" />
          Back to courses
        </Button>
      </div>
    );
  }

  // Remounting on identity keeps `defaultValues` honest: the form is built once
  // the record it edits is actually in hand.
  return <CourseForm key={id ?? 'new'} course={courseQuery.data} />;
}

function CourseForm({ course }: { course?: GetAdminCourse }) {
  const navigate = useNavigate();
  const isEdit = Boolean(course);

  const instructorsQuery = useData('GET /admin/instructors', { pageSize: 1000 });

  const createAction = useAction('POST /admin/courses', {
    invalidate: ['GET /admin/courses'],
  });
  const updateAction = useAction('PATCH /admin/courses/{id}', {
    invalidate: ['GET /admin/courses', 'GET /admin/courses/{id}'],
  });

  const form = useForm({
    defaultValues: toDefaults(course),
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          const payload = toPayload(value);
          if (course) {
            await updateAction.mutateAsync({ id: course.id, ...payload });
            toast.success('Course updated');
          } else {
            await createAction.mutateAsync(payload);
            toast.success('Course created');
          }
          await navigate('/courses');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  const instructorOptions = (instructorsQuery.data?.records ?? []).map(
    (instructor) => ({ value: instructor.id, label: instructor.name }),
  );

  return (
    <form
      className="flex min-h-0 w-full flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title={isEdit ? 'Edit Course' : 'Add Course'}
          description={
            isEdit
              ? 'Update the course details learners see.'
              : 'Create a new course. Unpublished courses stay hidden from learners.'
          }
          icon={BookOpen}
        />
        <div className="flex items-center gap-2 py-4">
          <Button type="button" variant="outline" onClick={() => navigate('/courses')}>
            <ArrowLeft className="me-1 size-4" />
            Back
          </Button>
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button type="submit" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? 'Saving...' : isEdit ? 'Save changes' : 'Create'}
              </Button>
            )}
          />
        </div>
      </div>

      <Tabs defaultValue="basics" className="w-full max-w-4xl">
        <TabsList>
          <TabsTrigger value="basics">Basics</TabsTrigger>
          <TabsTrigger value="schedule">Schedule</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="visibility">Visibility</TabsTrigger>
        </TabsList>

        <TabsContent value="basics" className="space-y-4 pt-4">
          <form.Field
            name="title"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Arabic 101"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="oneLiner"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="oneLiner">One-liner</Label>
                <Input
                  id="oneLiner"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="The single sentence shown under the title"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <form.Field
              name="category"
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="AI & Data Science"
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />

            <form.Field
              name="level"
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor="level">Level</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) =>
                      field.handleChange(value as typeof field.state.value)
                    }
                  >
                    <SelectTrigger id="level" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />

            <form.Field
              name="price"
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor="price">Price (SAR)</Label>
                  <Input
                    id="price"
                    type="number"
                    min={1}
                    step={0.01}
                    value={Number.isNaN(field.state.value) ? '' : field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
          </div>

          <form.Field
            name="languages"
            children={(field) => (
              <TextListField
                label="Languages"
                value={field.state.value}
                onChange={field.handleChange}
                placeholder="Arabic"
                addLabel="Add language"
                reorderable={false}
              />
            )}
          />

          <form.Field
            name="description"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={5}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="What the course covers"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="imageUrl"
            children={(field) => (
              <div className="max-w-md space-y-2">
                <ImageField
                  label="Course image"
                  value={field.state.value}
                  onChange={(imageUrl) => field.handleChange(imageUrl ?? '')}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="instructorIds"
            children={(field) => (
              <div className="max-w-md space-y-2">
                <Label>Instructors</Label>
                <MultiSelect
                  options={instructorOptions}
                  selectedValues={field.state.value}
                  onSelectionChange={field.handleChange}
                  placeholder="Attach instructors"
                  emptyMessage="No instructors yet. Add one from the Instructors page."
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="schedule" className="space-y-4 pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <form.Field
              name="startDate"
              children={(field) => (
                <div className="space-y-2">
                  <DateField
                    id="startDate"
                    label="Start date"
                    value={field.state.value}
                    onChange={field.handleChange}
                    placeholder="Coming soon"
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />

            <form.Subscribe
              selector={(state) => state.values.startDate}
              children={(startDate) => (
                <form.Field
                  name="endDate"
                  children={(field) => (
                    <div className="space-y-2">
                      <DateField
                        id="endDate"
                        label="End date"
                        value={field.state.value}
                        onChange={field.handleChange}
                        placeholder="Open ended"
                        minDate={startDate}
                      />
                      <FieldMessage errors={field.state.meta.errors} />
                    </div>
                  )}
                />
              )}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <form.Field
              name="durationHours"
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor="durationHours">Duration (hours)</Label>
                  <Input
                    id="durationHours"
                    type="number"
                    min={1}
                    step={1}
                    value={field.state.value ?? ''}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(
                        Number.isNaN(e.target.valueAsNumber)
                          ? null
                          : e.target.valueAsNumber,
                      )
                    }
                    placeholder="Leave empty to hide the duration"
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />

            <form.Field
              name="trainingMethod"
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor="trainingMethod">Course type</Label>
                  <Select
                    value={field.state.value ?? UNSET}
                    onValueChange={(value) =>
                      field.handleChange(
                        value === UNSET
                          ? null
                          : (value as 'ONLINE' | 'IN_PERSON'),
                      )
                    }
                  >
                    <SelectTrigger id="trainingMethod" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNSET}>Not specified</SelectItem>
                      {COURSE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
          </div>

          <form.Subscribe
            selector={(state) => state.values.trainingMethod}
            children={(trainingMethod) =>
              trainingMethod === 'IN_PERSON' && (
                <form.Field
                  name="location"
                  children={(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder="Venue and city"
                      />
                      <FieldMessage errors={field.state.meta.errors} />
                    </div>
                  )}
                />
              )
            }
          />
        </TabsContent>

        <TabsContent value="content" className="space-y-4 pt-4">
          <form.Field
            name="objectives"
            children={(field) => (
              <TextListField
                label="Learning objectives"
                value={field.state.value}
                onChange={field.handleChange}
                placeholder="What the learner will be able to do"
                addLabel="Add objective"
                multiline
              />
            )}
          />

          <form.Field
            name="targetedAudience"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="targetedAudience">Target audience</Label>
                <Textarea
                  id="targetedAudience"
                  rows={3}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Who the course is for"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="targetedAreas"
            children={(field) => (
              <TextListField
                label="Target areas"
                value={field.state.value}
                onChange={field.handleChange}
                placeholder="Nursing and Midwifery"
                addLabel="Add area"
              />
            )}
          />

          <form.Field
            name="prerequisites"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="prerequisites">Prerequisites</Label>
                <Textarea
                  id="prerequisites"
                  rows={3}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="What a learner needs before enrolling"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="visibility" className="space-y-4 pt-4">
          <form.Field
            name="published"
            children={(field) => (
              <ToggleRow
                name="published"
                label="Published"
                description="Published courses are visible to learners and can be purchased."
                checked={field.state.value}
                onCheckedChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          />
          <form.Field
            name="featured"
            children={(field) => (
              <ToggleRow
                name="featured"
                label="Featured"
                description="Featured courses are highlighted on the public home page."
                checked={field.state.value}
                onCheckedChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          />
          <form.Field
            name="comingSoon"
            children={(field) => (
              <ToggleRow
                name="comingSoon"
                label="Coming soon"
                description="Listed in the catalogue but its detail page stays unreachable."
                checked={field.state.value}
                onCheckedChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          />
          <form.Field
            name="showModules"
            children={(field) => (
              <ToggleRow
                name="showModules"
                label="Show modules"
                description="Reveals the module and lesson breakdown on the detail page."
                checked={field.state.value}
                onCheckedChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          />
        </TabsContent>
      </Tabs>

      <div className="max-w-4xl pt-4">
        <form.Subscribe
          selector={(state) => state.errors}
          children={(errors) => <FormMessage errors={errors} />}
        />
      </div>
    </form>
  );
}

function ToggleRow({
  name,
  label,
  description,
  checked,
  onCheckedChange,
  onBlur,
}: {
  name: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  onBlur: () => void;
}) {
  return (
    <div className="flex max-w-2xl items-center justify-between">
      <div className="space-y-1">
        <Label htmlFor={name}>{label}</Label>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      <Switch
        id={name}
        checked={checked}
        onBlur={onBlur}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}
